import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { cn } from '../cn';

export interface BarDatum {
  key: string;
  /** Texto del eje (puede ir vacío para no amontonar). */
  label: string;
  value: number;
  /** Barra apilada: partes con su color (suman value). */
  segments?: { value: number; color: string }[];
}

export interface BarTip {
  title: string;
  lines: string[];
}

/**
 * Barras con el detalle arriba de la barra al pasar el mouse (o tocarla en el celular), sin librerías de gráficos.
 * El texto se lee derecho, en una tarjeta que sigue a la barra.
 */
export function BarChart({
  data,
  tip,
  height = 220,
  ariaLabel,
  className,
}: {
  data: BarDatum[];
  tip: (index: number) => BarTip;
  height?: number;
  ariaLabel: string;
  className?: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  // Si la barra es alta y la tarjeta no entra arriba, va al costado de la barra (dentro del gráfico).
  const [side, setSide] = useState(false);
  const tipRef = useRef<HTMLDivElement>(null);
  const max = Math.max(...data.map((d) => d.value), 1);
  const n = Math.max(data.length, 1);
  const every = Math.ceil(n / 12);
  const pct = (v: number) => (v / max) * 100;

  const barPct = hover !== null && data[hover] ? pct(data[hover].value) : 0;
  useLayoutEffect(() => {
    const el = tipRef.current;
    if (!el || hover === null) return;
    setSide(el.offsetHeight + 10 + (barPct / 100) * height > height + 8);
  }, [hover, barPct, height]);

  let tipStyle: CSSProperties | undefined;
  let place = 'mid';
  if (hover !== null && data[hover]) {
    const x = ((hover + 0.5) / n) * 100;
    if (side) {
      // Al costado: a la derecha de la barra (a la izquierda si está sobre el final).
      place = x > 60 ? 'left' : 'right';
      const gap = `${(0.5 / n) * 100}% + 6px`;
      tipStyle = place === 'right' ? { left: `calc(${x}% + ${gap})`, top: 0 } : { right: `calc(${100 - x}% + ${gap})`, top: 0 };
    } else {
      place = x < 22 ? 'start' : x > 78 ? 'end' : 'mid';
      tipStyle = { left: `${x}%`, bottom: `calc(${barPct}% + 10px)` };
    }
  }
  const t = hover !== null && data[hover] ? tip(hover) : null;

  return (
    <div className={cn('bf-barchart', className)}>
      <div className="bf-barchart-plot" style={{ height }} role="img" aria-label={ariaLabel} onMouseLeave={() => setHover(null)}>
        {data.map((d, i) => (
          <div
            key={d.key}
            className={cn('bf-barchart-col', hover !== null && hover !== i && 'is-dim', hover === i && 'is-on')}
            onMouseEnter={() => setHover(i)}
            onClick={() => setHover((h) => (h === i ? null : i))}
          >
            <div className="bf-barchart-bar" style={{ height: d.value > 0 ? `max(${pct(d.value)}%, 3px)` : 0 }}>
              {d.segments?.map((s, j) => (s.value > 0 ? <span key={j} style={{ flexGrow: s.value, background: s.color }} /> : null))}
            </div>
          </div>
        ))}
        {t && (
          <div ref={tipRef} className={`bf-barchart-tip bf-barchart-tip--${place}`} style={tipStyle} aria-live="polite">
            <strong>{t.title}</strong>
            {t.lines.map((l) => (
              <span key={l}>{l}</span>
            ))}
          </div>
        )}
      </div>
      <div className="bf-barchart-axis" style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }} aria-hidden>
        {data.map((d, i) => (
          <span key={d.key}>{n > 14 ? (i % every === 0 ? d.label : '') : d.label}</span>
        ))}
      </div>
    </div>
  );
}
